import React, { useState, useEffect, useRef } from 'react';
import { Translation, getI18n } from 'react-i18next';
import { Button, Form, Modal, Upload, Typography, Space } from 'antd';

import { FileOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { documentCheck } from '../../../elements/components/Icons';
import Logger from '../../../../../lib/Logger';

import notification from '../../../elements/lib/NotificationWrapper';
import ResourceFormHeader from './MemberResourceFormHeader';
import ResourceFormStep1 from './MemberResourceFormStep1';
import ResourceFormStep2 from './MemberResourceFormStep2';
import ConfirmationModal from '../../../elements/components/ConfirmationModal';
import { mixpanelEventsEnum } from '../../../../../lib/Mixpanel';

const { Title } = Typography;

const layout = {
  main: {
    labelCol: { span: 23 },
    wrapperCol: { span: 23 },
  },
};

// upload criteria
const fileTypes = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
};
const fileSizeLimit = 1024 * 1024 * 6;
const reFileKey = /(?:(.*?)\/)?(.*?)\.(\w+)/;
const maxCount = 1;

const confirmTexts = {
  title: getI18n().t('resource_form_leave_title'),
  description: getI18n().t('resource_form_leave_description'),
  leaveText: getI18n().t('resource_form_leave_leave'),
  saveText: getI18n().t('resource_form_leave_save'),
};

const MemberResourceForm = ({
  partnershipId,
  isSubmitting,
  totalMembers,
  totalMembersFiltered,
  idsFiltered,
  loadTotalMembers,
  isLoading,
  loadResource,
  errors,
  data,
  trackerActions,
  ...props
}) => {
  const [form] = Form.useForm();
  const [isPinned, setIsPinned] = useState(false);
  const [isBeforeUpload, setIsBeforeUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [mediaId, setMediaId] = useState(null);
  const [isChangeValue, setIsChangeValue] = useState(false);
  const [value, setValue] = useState('');
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [optionMembers, setOptionMembers] = useState(1);
  const [classes, setClasses] = useState([]);
  const [careGuideIds, setCareGuideIds] = useState([]);
  const [careGuidePartnerTags, setCareGuidePartnerTags] = useState([]);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const fileMap = useRef({});

  const nextStep = async () => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.MEMBERS_ADMIN_NEXT_STEP_RESOURCE_FORM
    );
    const values = await form.validateFields();
    form
      .validateFields(['name'])
      .then(async () => {
        setFormData({ ...formData, ...values });
        setStep(step + 1);
      })
      .catch(() => {});
  };

  const prevStep = () => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.MEMBERS_ADMIN_PREV_STEP_RESOURCE_FORM
    );
    setStep(step - 1);
  };

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  const submitDataImage = (
    file,
    values,
    payload,
    resources_care_partner_tags
  ) => {
    props.getUploadUrl(
      file.name.split('.').pop(),
      'logos',
      async (success, upload_url, file_id) => {
        if (success) {
          // upload image
          props.upload(upload_url, file, async (uploadSuccess) => {
            const directory = 'logos/';
            file_id = file_id.replace(directory, '');
            if (uploadSuccess) {
              const matches = reFileKey.exec(file_id);
              const updateData = {
                file_directory: directory.slice(0, -1),
                file_name: matches[2],
                file_extension: matches[3],
              };

              // process image
              if (careGuideIds.length === 1) {
                await props.createImage(
                  careGuideIds[0],
                  partnershipId,
                  updateData,
                  async (createSuccess, imageId) => {
                    if (createSuccess) {
                      setMediaId(imageId);
                      payload['logo_id'] = imageId;
                      processData(values, payload, resources_care_partner_tags);
                    } else {
                      errorMessage();
                    }
                  }
                );
              } else {
                // process image
                await props.createImagePartnership(
                  partnershipId,
                  updateData,
                  async (createSuccess, imageId) => {
                    if (createSuccess) {
                      setMediaId(imageId);
                      payload['logo_id'] = imageId;
                      processData(values, payload, resources_care_partner_tags);
                    } else {
                      errorMessage();
                    }
                  }
                );
              }
            } else {
              errorMessage();
            }
          });
        } else {
          errorMessage();
        }
      }
    );
  };

  const processData = (values, payload, resources_care_partner_tags) => {
    let valuesForm = { ...formData, ...values };

    // transform values
    for (const key of Object.keys(data)) {
      // remove any non digit characters from strings
      if (
        ['telephone'].includes(key) &&
        valuesForm[key] &&
        valuesForm[key] !== null
      ) {
        payload[key] = valuesForm[key].replace(/\D/g, '');
      } else if (['is_pinned'].includes(key)) {
        payload[key] = valuesForm[key] ? true : false;
      } else if (['care_guides'].includes(key)) {
        payload['care_guide_ids'] = valuesForm[key];
      } else if (['service_tags'].includes(key)) {
        payload['service_tag_ids'] = valuesForm[key];
      } else if (
        ['telephone', 'website', 'email', 'postal_code'].includes(key)
      ) {
        if (valuesForm[key]) {
          payload[key] = valuesForm[key];
        }
      } else if (key in valuesForm) {
        payload[key] = valuesForm[key];
      }
    }

    for (const input of classes) {
      if (valuesForm[input]) {
        if (Array.isArray(valuesForm[input])) {
          const tagsFilters = valuesForm[input].filter((item) => item !== '');
          resources_care_partner_tags =
            resources_care_partner_tags.concat(tagsFilters);
        } else {
          if (valuesForm[input] !== '') {
            resources_care_partner_tags.push(valuesForm[input]);
          }
        }
      }
    }

    payload['is_multiple_care_guides'] = true;

    if (optionMembers === 2) {
      payload['is_multiple_care_guides'] = false;
    }

    payload['resources_care_partner_tags_list'] = resources_care_partner_tags;
    payload['partnership_id'] = partnershipId;
    payload['status'] = 1;

    delete payload['logo'];
    delete payload['resources_care_partner_tags'];

    props.create(payload, (success) => {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.MEMBERS_ADMIN_CREATE_RESOURCE_SUBMIT
      );
      if (success) {
        clearForm();
        notification.success(getI18n().t('feedback_form_success'));
      } else {
        notification.error(getI18n().t('feedback_form_error'));
      }
    });
  };

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `ResourceForm.submitData(###)`);
    const payload = {};
    let resources_care_partner_tags = [];

    if (uploadedFile !== null) {
      if (mediaId) {
        payload['logo_id'] = mediaId;
        processData(values, payload, resources_care_partner_tags);
      } else {
        const file = uploadedFile[0];
        submitDataImage(file, values, payload, resources_care_partner_tags);
      }
    } else {
      payload['logo_id'] = null;
      processData(values, payload, resources_care_partner_tags);
    }
  };

  const clearForm = async () => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.MEMBERS_ADMIN_CLOSE_RESOURCE_FORM_MODAL
    );
    props.hideForm();
    props.formDestroy();
    form.setFieldsValue({
      name: '',
      description: '',
      telephone: '',
      website: '',
      email: '',
      postal_code: '',
      service_tags: [],
      is_event_partner: false,
      is_pinned: false,
      care_guides: [],
      logo: null,
      resources_care_partner_tags: [],
    });
    for (const key of Object.keys(data)) {
      form.setFields([{ name: key, errors: [] }]);
    }
    fileMap.current = {};

    setIsBeforeUpload(true);
    setMediaId(null);

    setUploadedFile(null);
    setIsBeforeUpload(false);
    setMediaId(null);
    setIsChangeValue(false);
    setStep(0);
    for (const input of classes) {
      form.setFieldsValue({ [input]: [] });
    }
    setClasses([]);
    setCareGuideIds([]);
    setCareGuidePartnerTags([]);
    setOptionMembers(1);
    setValue('');
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `ResourceForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `ResourceForm.handleFinishFailed(###)`);
    notification.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  // remove error message when input value changes
  const handleValuesChange = (changedValues, allValues) => {
    setIsChangeValue(true);
    const nameValues = Object.keys(changedValues);
    for (const key of nameValues) {
      form.setFields([{ name: key, errors: [] }]);
    }
    let resources_care_partner_tags = [];
    if (optionMembers === 2 && step === 1) {
      for (const input of classes) {
        if (form.getFieldsValue()[input]) {
          if (Array.isArray(form.getFieldsValue()[input])) {
            const tagsFilters = form
              .getFieldsValue()
              [input].filter((item) => item !== '');
            resources_care_partner_tags =
              resources_care_partner_tags.concat(tagsFilters);
          } else {
            if (form.getFieldsValue()[input] !== '') {
              resources_care_partner_tags.push(form.getFieldsValue()[input]);
            }
          }
        }
      }
      const filter = {
        care_guide_partner_tags: resources_care_partner_tags,
      };
      setCareGuidePartnerTags(resources_care_partner_tags);
      if (resources_care_partner_tags.length > 0) {
        loadTotalMembers(partnershipId, filter);
      } else {
        loadTotalMembers(partnershipId, {});
      }
    }
  };

  const onClickOK = () => {
    form.submit();
  };

  const errorMessage = () => {
    notification.error(
      getI18n().t('resource_form_feedback_image_upload_error')
    );
  };

  const onBeforeUpload = (file) => {
    const isUnderMaxCount = Object.keys(fileMap.current).length < maxCount;
    if (!isUnderMaxCount) {
      form.setFields([
        {
          name: 'doc-file',
          errors: [
            <Space>
              <ExclamationCircleOutlined />
              {getI18n().t('resource_form_feedback_image_file_types')}
            </Space>,
          ],
        },
      ]);

      setUploadedFile(null);
      return Upload.LIST_IGNORE;
    }

    const isSupportedFileType = Object.keys(fileTypes).includes(file.type);
    if (!isSupportedFileType) {
      form.setFields([
        {
          name: 'doc-file',
          errors: [
            <Space>
              <ExclamationCircleOutlined />
              {getI18n().t('resource_form_feedback_image_file_types')}
            </Space>,
          ],
        },
      ]);

      setUploadedFile(null);
      return Upload.LIST_IGNORE;
    }

    const isUnderFileSizeLimit = file.size < fileSizeLimit;
    if (!isUnderFileSizeLimit) {
      form.setFields([
        {
          name: 'doc-file',
          errors: [
            <Space>
              <ExclamationCircleOutlined />
              {getI18n().t('resource_form_feedback_image_file_size_limit')}
            </Space>,
          ],
        },
      ]);

      setUploadedFile(null);
      return Upload.LIST_IGNORE;
    }

    if (isSupportedFileType && isUnderFileSizeLimit) {
      setUploadedFile([file]);
      setIsBeforeUpload(true);
      setMediaId(null);
      return false;
    }
    return Upload.LIST_IGNORE;
  };

  const handleFileChange = (info) => {
    if (info.file.status === 'removed') {
      setUploadedFile(null);
      setIsBeforeUpload(false);
      if (mediaId) {
        setMediaId(null);
      }
    } else {
      setUploadedFile([info.file]);
      form.setFieldsValue({
        'doc-file': info.file,
      });
    }
  };

  const uploadIcon = () => {
    return isBeforeUpload ? documentCheck() : <FileOutlined />;
  };

  const onChangePinned = (e) => {
    setIsPinned(e.target.checked);
    setIsChangeValue(true);
  };

  const onHandleChangeOptionMembers = (e) => {
    setOptionMembers(e.target.value);
    if (e.target.value === 1) {
      loadTotalMembers(partnershipId, {});
    } else {
      if (careGuidePartnerTags.length > 0) {
        loadTotalMembers(partnershipId, {
          care_guide_partner_tags: careGuidePartnerTags,
        });
      }
    }
  };

  const handleConfirmLeave = () => {
    setConfirmationModalOpen(false);
  };

  const handleConfirmClose = () => {
    clearForm();
    setConfirmationModalOpen(false);
  };

  const leaveModal = () => {
    if (isChangeValue) {
      setConfirmationModalOpen(true);
    } else {
      clearForm();
    }
  };

  useEffect(() => {
    if (careGuidePartnerTags.length > 0) {
      loadTotalMembers(partnershipId, {
        care_guide_partner_tags: careGuidePartnerTags,
      });
    } else {
      loadTotalMembers(partnershipId, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careGuidePartnerTags]);

  useEffect(() => {
    if (props.visible) {
      setCareGuideIds(data['care_guides']);
      form.setFieldsValue({
        care_guides: data['care_guides'],
      });
      if (data['care_guides'].length > 0) {
        setOptionMembers(2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible]);

  return (
    <Translation>
      {(t) => (
        <>
          <Modal
            title={
              <Title level={3} className='resource-note-modal-title'>
                {t('resource_form_title_create')}
              </Title>
            }
            centered
            visible={props.visible}
            onOk={onClickOK}
            onCancel={leaveModal}
            footer={[
              step === 0 && (
                <Button
                  key='close'
                  size='large'
                  className='resource-form-btn'
                  onClick={leaveModal}
                >
                  {t('action_close')}
                </Button>
              ),

              step === 0 && (
                <Button
                  type='primary'
                  size='large'
                  loading={isSubmitting}
                  onClick={nextStep}
                >
                  {t('resource_form_button_next_step')}
                </Button>
              ),
              step === 1 && (
                <Button
                  size='large'
                  className='resource-form-btn'
                  onClick={prevStep}
                >
                  {t('resource_form_button_previous_step')}
                </Button>
              ),

              step === 1 && (
                <Button
                  disabled={!isChangeValue}
                  key='submit'
                  type='primary'
                  size='large'
                  loading={isSubmitting}
                  onClick={onClickOK}
                >
                  {t('resource_form_button_add_submit')}
                </Button>
              ),
            ]}
            className='resource-form-modal'
            width={1100}
            forceRender={true}
          >
            <div className='resource-form'>
              <ResourceFormHeader step={step} />
              <Form
                name='resource_form'
                form={form}
                initialValues={data}
                onFinish={handleFinish}
                onFinishFailed={handleFinishFailed}
                onValuesChange={handleValuesChange}
                validateTrigger='onSubmit'
                layout='vertical'
                {...layout.main}
              >
                {step === 0 && (
                  <ResourceFormStep1
                    form={form}
                    data={data}
                    handleFinish={handleFinish}
                    handleFinishFailed={handleFinishFailed}
                    isBeforeUpload={isBeforeUpload}
                    uploadIcon={uploadIcon}
                    handleFileChange={handleFileChange}
                    uploadedFile={uploadedFile}
                    onBeforeUpload={onBeforeUpload}
                    isPinned={isPinned}
                    setIsPinned={setIsPinned}
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    value={value}
                    setValue={setValue}
                    isChangeValue={isChangeValue}
                    setIsChangeValue={setIsChangeValue}
                    handleValuesChange={handleValuesChange}
                    onChangePinned={onChangePinned}
                  />
                )}

                {step === 1 && (
                  <ResourceFormStep2
                    form={form}
                    optionMembers={optionMembers}
                    partnershipId={partnershipId}
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    totalMembers={totalMembers}
                    totalMembersFiltered={totalMembersFiltered}
                    classes={classes}
                    setClasses={setClasses}
                    setCareGuideIds={setCareGuideIds}
                    onHandleChangeOptionMembers={onHandleChangeOptionMembers}
                    careGuidePartnerTags={careGuidePartnerTags}
                    idsFiltered={idsFiltered}
                    careGuideIds={careGuideIds}
                  />
                )}

                <div style={{ display: 'none' }}>
                  <Button htmlType='submit'></Button>
                </div>
              </Form>
            </div>

            <ConfirmationModal
              texts={confirmTexts}
              openConfirmationModal={confirmationModalOpen}
              onLeaveConfirmation={handleConfirmClose}
              onSaveConfirmation={handleConfirmLeave}
              onCancel={handleConfirmLeave}
              setConfirmationModalOpen={setConfirmationModalOpen}
            />
          </Modal>
        </>
      )}
    </Translation>
  );
};

export default MemberResourceForm;

Logger.log('silly', `MemberResourceForm loaded.`);
